import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Toaster } from "react-hot-toast";
import * as Icons from "heroicons-react";
import { signInUser } from "../Utility/Auth";
import Header from "../Common/Header/Header";
import SideBar from "../Common/SideBar/SideBar";
import { notify } from "../Utility/Notify";
import {
  useSettingsQuery,
  useAddSettingsMutation,
} from "../../services/settingsApi";

const PaymentSettings = () => {
  const user = signInUser();
  const { aamarId } = user;
  const language = useSelector((state) => state.languageReducer);
  const lang = language?.language;

  const { data: settings, isSuccess } = useSettingsQuery(aamarId);
  const [addSettings] = useAddSettingsMutation();

  const [selectedPayments, setSelectedPayments] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [paymentFormData, setPaymentFormData] = useState({
    id: "",
    order: "",
    name: "",
    type: "mfs",
    status: true,
  });

  useEffect(() => {
    if (isSuccess && settings) {
      if (settings?.paymentMethods && settings?.paymentMethods.length > 0) {
        setSelectedPayments(settings?.paymentMethods);
      } else {
        setSelectedPayments([
          { id: "bkash", order: "bKash", name: "bKash", type: "mfs", status: true },
          { id: "nagad", order: "nagad", name: "Nagad", type: "mfs", status: true },
          { id: "rocket", order: "rocket", name: "Rocket", type: "mfs", status: true },
          { id: "upay", order: "upay", name: "Upay", type: "mfs", status: true },
          { id: "visa", order: "visa", name: "Visa", type: "card", status: true },
          { id: "dbbl", order: "dbbl", name: "DBBL", type: "card", status: true },
          { id: "mtb", order: "mtb", name: "MTB", type: "card", status: true },
          { id: "amex", order: "amex", name: "AMEX", type: "card", status: true },
          { id: "ebl", order: "ebl", name: "EBL", type: "card", status: true },
          { id: "brac", order: "brac", name: "BRAC", type: "card", status: true },
          { id: "masterCard", order: "masterCard", name: "MasterCard", type: "card", status: true },
        ]);
      }
    }
  }, [isSuccess, settings]);

  const handlePaymentFormSubmit = (e) => {
    e.preventDefault();
    if (!paymentFormData.name.trim() || !paymentFormData.order.trim()) {
      notify("Please fill all required fields", "error");
      return;
    }

    const orderKey = paymentFormData.order.trim();

    if (editingMethod) {
      const updated = selectedPayments.map((m) =>
        (m.id && m.id === editingMethod.id) || m.order === editingMethod.order
          ? { ...paymentFormData, order: orderKey }
          : m
      );
      setSelectedPayments(updated);
    } else {
      const exists = selectedPayments.some(
        (m) => m.order.toLowerCase() === orderKey.toLowerCase()
      );
      if (exists) {
        notify("Payment method code already exists", "error");
        return;
      }
      const newMethod = {
        ...paymentFormData,
        id: `pay-${Date.now()}`,
        order: orderKey,
      };
      setSelectedPayments([...selectedPayments, newMethod]);
    }
    setShowPaymentModal(false);
    setPaymentFormData({ id: "", order: "", name: "", type: "mfs", status: true });
    setEditingMethod(null);
  };

  const handleSavePaymentSettings = async (e) => {
    e.preventDefault();
    
    // Strip out Mongoose metadata/immutable fields to avoid database write errors
    const { _id, createdAt, updatedAt, __v, ...cleanSettings } = settings || {};
    
    const payload = {
      ...cleanSettings,
      aamarId: aamarId,
      paymentMethods: selectedPayments,
    };

    try {
      const response = await addSettings(payload).unwrap();
      if (response.status === "success") {
        notify("Payment Settings updated successfully!", "success");
      } else {
        notify("Something went wrong", "error");
      }
    } catch (error) {
      notify(error.message || "Failed to update payment settings", "error");
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-2">
          <SideBar />
        </div>
        <div className="col-md-10">
          <Header title={lang?.paymentSettings || "Payment Settings"} />
          <div className="row mt-3">
            <div className="col-md-1" />
            <div className="col-md-10 mt-3">
              <div className="card shadow-sm p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="d-flex align-items-center gap-2 m-0 fw-bold">
                    <Icons.CreditCardOutline />
                    {lang?.paymentSettings || "Payment Settings"}
                  </h5>
                  <Button
                    variant="dark"
                    size="sm"
                    onClick={() => {
                      setEditingMethod(null);
                      setPaymentFormData({
                        id: "",
                        order: "",
                        name: "",
                        type: "mfs",
                        status: true,
                      });
                      setShowPaymentModal(true);
                    }}
                  >
                    Add Payment Method
                  </Button>
                </div>

                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Code / Order</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(selectedPayments) && selectedPayments.length > 0 ? (
                        selectedPayments.map((method, index) => (
                          <tr key={method.id || index}>
                            <td>{method.name}</td>
                            <td>
                              <span className="badge bg-secondary text-capitalize">
                                {method.type === "mfs"
                                  ? "Mobile Wallet (MFS)"
                                  : method.type === "card"
                                  ? "Card"
                                  : "Cash"}
                              </span>
                            </td>
                            <td>
                              <code>{method.order}</code>
                            </td>
                            <td>
                              <Form.Check
                                type="switch"
                                id={`method-status-${method.id || index}`}
                                checked={method.status !== false}
                                onChange={(e) => {
                                  const updated = selectedPayments.map((m, idx) =>
                                    idx === index || (m.id && m.id === method.id)
                                      ? { ...m, status: e.target.checked }
                                      : m
                                  );
                                  setSelectedPayments(updated);
                                }}
                              />
                            </td>
                            <td>
                              <Button
                                variant="outline-dark"
                                size="sm"
                                className="me-2"
                                onClick={() => {
                                  setEditingMethod(method);
                                  setPaymentFormData({ ...method });
                                  setShowPaymentModal(true);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => {
                                  if (window.confirm(`Delete ${method.name}?`)) {
                                    const updated = selectedPayments.filter(
                                      (m, idx) =>
                                        !(idx === index || (m.id && m.id === method.id))
                                    );
                                    setSelectedPayments(updated);
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center text-muted p-3">
                            No payment methods configured.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-end mt-4">
                  <Button variant="dark" onClick={handleSavePaymentSettings}>
                    <Icons.SaveOutline className="me-2" />
                    Save Payment Settings
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Toaster position="bottom-right" />

      {/* Payment Method Add/Edit Modal */}
      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} centered>
        <Form onSubmit={handlePaymentFormSubmit}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">
              {editingMethod ? "Edit Payment Method" : "Add Payment Method"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Method Name</Form.Label>
              <Form.Control
                type="text"
                required
                value={paymentFormData.name}
                onChange={(e) => {
                  const name = e.target.value;
                  const order = editingMethod
                    ? paymentFormData.order
                    : name.toLowerCase().replace(/[^a-z0-9]/g, "");
                  setPaymentFormData({ ...paymentFormData, name, order });
                }}
                placeholder="e.g. Bkash Personal, Visa Card"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Method Code / Order Key</Form.Label>
              <Form.Control
                type="text"
                required
                value={paymentFormData.order}
                onChange={(e) =>
                  setPaymentFormData({
                    ...paymentFormData,
                    order: e.target.value.replace(/\s+/g, ""),
                  })
                }
                placeholder="e.g. bkash, visa, cash"
              />
              <Form.Text className="text-muted">
                Unique identifier used in system logs. No spaces.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Payment Type</Form.Label>
              <Form.Select
                value={paymentFormData.type}
                onChange={(e) =>
                  setPaymentFormData({ ...paymentFormData, type: e.target.value })
                }
              >
                <option value="mfs">Mobile Financial Service (MFS)</option>
                <option value="card">Card</option>
                <option value="cash">Cash</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Is Active"
                checked={paymentFormData.status}
                onChange={(e) =>
                  setPaymentFormData({ ...paymentFormData, status: e.target.checked })
                }
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-dark" onClick={() => setShowPaymentModal(false)}>
              Cancel
            </Button>
            <Button variant="dark" type="submit">
              Save Method
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default PaymentSettings;
